import { TeamPreview, UserPreview } from '@main/access-control-types';
import { mkInput, mkOrder, mkType, SchemaToType } from '@main/schema-utils';

import { Region } from './dataSilo';
import {
  Controllership,
  ProcessingActivityOrderField,
  RetentionType,
} from './enums';

export const ProcessingActivitiesFiltersInput = mkInput({
  name: 'ProcessingActivitiesFiltersInput',
  comment: 'Filters for processing activities',
  fields: {
    text: {
      comment: 'Find any processing activities are iLike a text string',
      optional: true,
      type: 'string',
    },
    teamIds: {
      comment: 'The ids of the teams that own the processing activities',
      type: 'id',
      modelName: 'team',
      list: true,
      optional: true,
    },
    ownerIds: {
      comment: 'The ids of the users that own the processing activities',
      type: 'id',
      modelName: 'user',
      list: true,
      optional: true,
    },
  },
});

/** Override type */
export type ProcessingActivitiesFiltersInput = SchemaToType<
  typeof ProcessingActivitiesFiltersInput
>;

export const ProcessingActivity = mkType({
  name: 'ProcessingActivity',
  comment: 'A processing activity of the organization',
  fields: {
    id: {
      comment: 'The unique ID of the processing activity',
      modelName: 'processingActivity',
      type: 'id',
    },
    title: {
      comment: 'The title of the processing activity',
      type: 'string',
    },
    description: {
      comment: 'The description of the processing activity',
      type: 'string',
    },
    securityMeasureDetails: {
      comment: 'Security measures related to this processing activity',
      type: 'string',
      optional: true,
    },
    controllerships: {
      comment: 'The controllership values assigned to this processingActivity',
      type: { Controllership },
      list: true,
    },
    storageRegions: {
      comment: 'The storage regions associated with this processing activity',
      type: Region,
      list: true,
    },
    transferRegions: {
      comment: 'The transfer regions associated with this processing activity',
      type: Region,
      list: true,
    },
    retentionType: {
      comment: 'The type of retention schedule',
      type: { RetentionType },
    },
    retentionPeriod: {
      comment:
        'The number of days personal data is retained (for stated period retention types)',
      type: 'int',
      optional: true,
    },
    teams: {
      comment:
        'The list of teams who are responsible for managing this processing activity',
      list: true,
      type: TeamPreview,
    },
    owners: {
      comment:
        'The list of users who are responsible for managing this processing activity',
      list: true,
      type: UserPreview,
    },
  },
});

/** Override type */
export type ProcessingActivity = SchemaToType<typeof ProcessingActivity>;

/** Order for a processingActivities query */
export const ProcessingActivityOrder = mkOrder(
  ProcessingActivity.name,
  ProcessingActivityOrderField,
);

/** Override type */
export type ProcessingActivityOrder = SchemaToType<
  typeof ProcessingActivityOrder
>;
